<template>
  <EChartCard
    :title="$t('passRateTrend')"
    :subtitle="$t('passRateTrendSubtitle')"
    :buildOption="buildOption"
    :height="360"
  />
</template>

<script setup>
import { useStore } from 'vuex';
import EChartCard from '@/components/charts/base/EChartCard.vue';
import { getCategorical } from '@/utils/vizPalette';
import { axisColor } from '@/utils/chartTheme';
import { buildMonthAxis, monthLabel } from '@/utils/dateAxis';

const store = useStore();

// Per INSPECTION RECORD, not per asset: an asset inspected four times
// contributes four points to the trend.
const query = `
  SELECT
    strftime(effectiveInspectionDate, '%Y-%m') AS month,
    AVG(passRate) AS value
  FROM filteredItems
  WHERE effectiveInspectionDate IS NOT NULL AND passRate IS NOT NULL
  GROUP BY 1
  ORDER BY 1
`;

const buildOption = async () => {
  const conn = store.getters.getDBConnection;
  if (!conn) return null;

  const result = await conn.query(query);
  const rows = result.toArray().map((row) => {
    const json = row.toJSON();
    return { month: json.month, value: Number(json.value) };
  });
  if (rows.length === 0) return null;

  const recent = rows.slice(-12);
  const values = {};
  recent.forEach((row) => {
    values[row.month] = row.value;
  });

  // A continuous axis between the first and last month with data; months in
  // between with no inspections stay null and are bridged by connectNulls.
  const months = buildMonthAxis(recent[0].month, recent[recent.length - 1].month);

  const fontColor = store.getters.chartFontColor;
  const lineColor = getCategorical(store.state.theme)[0];
  const gridColor = axisColor(store.state.theme);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      valueFormatter: (value) => (value === null || value === undefined ? '-' : `${value.toFixed(1)}%`),
    },
    grid: { left: 8, right: 16, top: 16, bottom: 4, containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: months.map(monthLabel),
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { show: false },
      axisLabel: { color: fontColor, hideOverlap: true },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { color: fontColor, formatter: '{value}%' },
      splitLine: { lineStyle: { color: gridColor } },
    },
    series: [
      {
        type: 'line',
        smooth: true,
        connectNulls: true,
        symbolSize: 8,
        lineStyle: { width: 2, color: lineColor },
        itemStyle: { color: lineColor },
        data: months.map((month) => (month in values ? values[month] : null)),
      },
    ],
  };
};
</script>
